<template>
  <VDialogV3
    id="order-position-set-delivery-dates-dialog"
    title="Желаемые даты"
    :entity="localPosition"
    validate-on-mount
    width="500px"
    no-padding
    @submit="save"
  >
    <section class="reference-info">
      <article>
        Срок доставки {{ localPosition.deliveryDays }}
        {{ $core.utils.regional.ru.pluralize(localPosition.deliveryDays, 'день', 'дня', 'дней') }}
      </article>
      <article v-if="localPosition.requestDate">
        Дата заявки: {{ $core.utils.date.format(localPosition.requestDate) }}
      </article>
    </section>

    <section>
      <template v-if="localPosition.isPreorder">
        <VDateRangeInputNew
          v-model="localPosition.preorderShipmentPeriod"
          label="Период доставки"
          layout="vertical"
          required
          input-layout="input"
          :validate-method="(value) => localPosition.validatePreorderShipmentPeriodFrom(value)"
        />
      </template>

      <template v-else>
        <VDateInput
          v-model="localPosition.desiredReceiptDate"
          label="Укажите желаемую дату доставки"
          layout="vertical"
          required
          :validate-method="(value) => localPosition.validateDesiredReceiptDate(value)"
          :warning-method="(value) => warnWeekend(value)"
        >
          <template #helper-text>
            {{ $core.utils.date.getWeekDayName(localPosition.desiredReceiptDate) }}
          </template>
        </VDateInput>

        <VDateInput
          v-model="localPosition.plannedShipmentDate"
          label="Укажите планируемую дату отгрузки"
          layout="vertical"
          required
          :warning-method="(value) => warnWeekend(value)"
        >
          <template #helper-text>
            {{ $core.utils.date.getWeekDayName(localPosition.plannedShipmentDate) }}
          </template>
        </VDateInput>
      </template>
    </section>

    <section v-if="showCurrencyCode">
      <VSelect
        v-model="localCurrency"
        label="Валюта"
        layout="vertical"
        :options="currenciesOptions"
        option-label="code"
        option-description="symbol"
        required
      />
    </section>

    <section v-if="showReasonInput">
      <VTextarea
        v-model="reasonText"
        label="Причина редактирования"
        layout="vertical"
        :max-length="500"
      />
    </section>
  </VDialogV3>
</template>

<script lang="ts">
import Order            from '@/domains/Orders/Order';
import OrderPosition    from '@/domains/Orders/OrderPosition';
import OrdersService    from '@/domains/Orders/OrdersService';
import Preorder         from '@/domains/Preorders/Preorder';
import PreorderPosition from '@/domains/Preorders/PreorderPosition';

type Currency = {
  code:   string;
  symbol: string;
};

export default defineComponent({
  name: 'OrderPositionSetDeliveryDatesDialog',

  props: {
    /**
     * Заказ целиком. При передаче диалог работает в массовом режиме —
     * выставленные даты применяются ко всем позициям заказа.
     * basePosition в этом режиме — первая позиция (источник дефолтов).
     */
    order: {
      type:    Object as PropType<Order | Preorder>,
      default: null,
    },
    /**
     * Конкретная позиция. При передаче диалог работает в одиночном режиме —
     * даты выставляются только этой позиции.
     */
    position: {
      type:    Object as PropType<OrderPosition | PreorderPosition>,
      default: null,
    },
    /** Показать селект выбора валюты (вкл. в импорте, где валюта ещё не задана). */
    showCurrencyCode: {
      type:    Boolean,
      default: false,
    },
    /**
     * Показать поле "Причина редактирования".
     * Применимо только в одиночном режиме при редактировании существующей позиции —
     * причина уходит вторым параметром в OrdersService.updatePosition.
     */
    showReasonInput: {
      type:    Boolean,
      default: false,
    },
    /**
     * Сохранять ли изменения на бэк сразу после нажатия «Сохранить».
     * Выключается там, где документ ещё редактируется в карточке: позиции
     * могут быть новыми или пришедшими из истории, и отправлять их отдельным
     * запросом рано — они уедут вместе с документом при его сохранении.
     */
    saveToServer: {
      type:    Boolean,
      default: true,
    },
  },

  emits: ['save'],

  data() {
    /**
     * basePosition — оригинальная позиция-источник.
     * localPosition — её клон, с которым работает форма (binding через v-model).
     * Клонируем, чтобы пользователь мог отменить изменения, и они не утекли в оригинал.
     *
     * Сеттеры desiredReceiptDate/plannedShipmentDate на самой позиции
     * пересчитывают парную дату автоматически — никакой локальной механики не нужно.
     */
    const basePosition: OrderPosition | PreorderPosition = this.order
      ? this.order.positions?.[0]
      : this.position;

    return {
      basePosition,
      localPosition: this.$core.utils.cloneDeep(basePosition),
      reasonText:    null as string | null,
    };
  },

  computed: {
    /**
     * Селект валюты. В режиме заказа меняем валюту и заказа и всех его позиций.
     * В режиме одиночной позиции — только её валюту.
     */
    localCurrency: {
      get(): Currency {
        if (this.order) {
          return this.$core.utils.money.getCurrencyByCode(this.order.currencyCode);
        }
        return this.$core.utils.money.getCurrencyByCode(this.localPosition.currencyCode);
      },
      set({ code }: Currency) {
        if (this.order) {
          this.order.currencyCode = code;
          this.order.positions?.forEach(pos => {
            pos.currencyCode = code;
          });
        }
        else {
          this.localPosition.currencyCode = code;
        }
      },
    },

    currenciesOptions(): Currency[] {
      return Array.from(this.$core.currencies.currencies);
    },
  },

  methods: {
    /**
     * Валидация-варнинг: проверяет, выпадает ли дата на выходной.
     * Возвращает текстовое предупреждение или null. Контрол сам показывает
     * варнинг в messages-row жёлтым.
     */
    warnWeekend(value: unknown): string | null {
      if (!value) return null;
      if (this.$core.utils.date.isWeekend(value) === false) return null;
      return `Выпадает на выходной день (${this.$core.utils.date.getWeekDayName(value, true, true)})`;
    },

    /**
     * Сохранение. Применяем поля локальной копии в оригинал и, если разрешено
     * пропом saveToServer, дёргаем сервис обновления.
     *
     * Валидация уже прошла на стороне VDialogV3.onSubmitButtonClicked — сюда
     * попадаем только если форма валидна.
     */
    async save(): Promise<void> {
      let isOk;
      if (this.order) {
        // Применяем поля локальной копии ко всем позициям оригинального заказа.
        if (this.order.isPreorder) {
          this.order.positions?.forEach(pos => {
            pos.preorderShipmentPeriod = this.localPosition.preorderShipmentPeriod;
          });
        }
        else {
          this.order.positions?.forEach(pos => {
            pos.desiredReceiptDate  = this.localPosition.desiredReceiptDate;
            pos.plannedShipmentDate = this.localPosition.plannedShipmentDate;
          });
        }

        if (this.saveToServer === true) {
          isOk = await OrdersService.updateOrder(this.order);
        }
      }
      else {
        // Применяем поля локальной копии в оригинальную позицию.
        if (this.basePosition.isPreorder) {
          this.basePosition.preorderShipmentPeriod = this.localPosition.preorderShipmentPeriod;
        }
        else {
          this.basePosition.desiredReceiptDate  = this.localPosition.desiredReceiptDate;
          this.basePosition.plannedShipmentDate = this.localPosition.plannedShipmentDate;
        }

        if (this.saveToServer === true) {
          isOk = await OrdersService.updatePosition(this.basePosition, this.reasonText ?? undefined);
        }
      }

      if (isOk) this.$core.showNotification.success('Даты обновлены');

      this.$emit('save');
      this.$core.dialogs.OrderPositionSetDeliveryDatesDialog.hide();
    },
  },
});
</script>
